import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useMemo} from 'react';

import {LLMRole} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getAskUserReply} from 'llm/chat-library/ask-user/AskUserUtils';
import ChatAssistantMessageBubble from 'llm/chat-library/messages/ChatAssistantMessageBubble';
import {countUniqueUserMessageSenders, MessageCreator} from 'llm/chat-library/messages/ChatMessageSender';
import {ChatMessageSenderContext} from 'llm/chat-library/messages/ChatMessageSenderContext';
import ChatPendingVoiceTranscriptBubble from 'llm/chat-library/messages/ChatPendingVoiceTranscriptBubble';
import {ChatProgressMessageBubble} from 'llm/chat-library/messages/ChatProgress';
import ChatUserMessageBubble from 'llm/chat-library/messages/ChatUserMessageBubble';
import {ClientChatEvent, ClientDisplayChatEvent} from 'llm/chat-library/messages/MessageTypes';
import {
  filterDisplayEvents,
  getAnsweredApprovalStatus,
  getFirstDisplayMessageKey,
} from 'llm/chat-library/messages/MessageUtils';
import {ProgressData} from 'llm/chat-library/progress/ProgressTypes';
import {ChatEmptyState} from 'llm/chat-library/sidepanel/ChatEmptyState';
import {LatestUserMessageInfo} from 'llm/ChatScrollTypes';
import {assertNever} from 'util/Types';
export interface ChatMessageThreadProps {
  events: Array<ClientChatEvent>;
  chatSessionId: string;
  /** Display name for the chat session, when known (used for default save labels). */
  chatSessionDisplayName?: string | null;
  onUpdateLatestUserMessageInfo?: ((info: LatestUserMessageInfo) => void) | null;
  banner: React.ReactNode;
  actionButton: React.ReactNode;
  readonly: boolean;
  hasChatScopedWriteApproval: boolean;
  /**
   * Summary of tool calls used in the chat.
   *
   * This is currently only used by deep research, and if present,
   * certain UIs may render that mention "Deep research", which
   * need to be generalized by using agentType.
   *
   * TODO: Make required to support agent switching.
   */
  progressData: ProgressData | null;
  /** Unfiltered tool-call data used for report citation details. */
  citationData?: ProgressData | null;
  messageSenderContext?: ChatMessageSenderContext | null;
  /** Chat session creator; used as sender fallback when the routine runs as creator. */
  sessionCreatorFallback?: MessageCreator | null;
}

function getEventKey(event: ClientDisplayChatEvent, idx: number): string {
  switch (event.__typename) {
    case 'ClientStreamingAssistantMessage': {
      return `streaming_${event.responseToEventId}`;
    }
    case 'ClientOptimisticUserMessage': {
      return `optimistic_${idx}`;
    }
    case 'ClientPendingVoiceTranscript': {
      return `voice_transcript_${event.itemId}`;
    }
    case 'ClientProgressMessage': {
      return `progress_${idx}`;
    }
    default:
      return event.id;
  }
}

/** @internal exported for testing */
export const _testingPerfMetrics = {
  onRender: (): void => {},
};

/**
 * Display for the current chat conversation.
 */
export const ChatMessageThread = React.memo(
  ({
    events,
    chatSessionId,
    chatSessionDisplayName = null,
    onUpdateLatestUserMessageInfo,
    banner,
    actionButton,
    readonly,
    hasChatScopedWriteApproval,
    progressData,
    citationData,
    messageSenderContext = null,
    sessionCreatorFallback = null,
  }: ChatMessageThreadProps): JSX.Element => {
    _testingPerfMetrics.onRender();
    const displayEvents = filterDisplayEvents(events);

    const firstDisplayMessageKey = useMemo(
      () => getFirstDisplayMessageKey(displayEvents, getEventKey),
      [displayEvents]
    );

    const showCurrentUserSender = useMemo(() => {
      if (messageSenderContext == null) {
        return false;
      }
      return countUniqueUserMessageSenders(displayEvents, messageSenderContext, sessionCreatorFallback) >= 2;
    }, [displayEvents, messageSenderContext, sessionCreatorFallback]);

    const lastUserMessageIndex = displayEvents.findLastIndex((event) => event.role === LLMRole.USER);
    const updateLatestUserMessageElement = useCallback(
      (element: HTMLDivElement) => {
        if (onUpdateLatestUserMessageInfo) {
          onUpdateLatestUserMessageInfo({index: lastUserMessageIndex, element});
        }
      },
      [lastUserMessageIndex, onUpdateLatestUserMessageInfo]
    );

    return (
      <div className={css(styles.threadContainer)}>
        {banner}
        {displayEvents.length === 0 && <ChatEmptyState readonly={readonly} />}
        {displayEvents.map((event, index) => {
          if (event.__typename === 'ClientProgressMessage') {
            return (
              <ChatProgressMessageBubble
                key={getEventKey(event, index)}
                message={event}
                progressData={progressData}
                chatSessionId={chatSessionId}
              />
            );
          }
          if (event.__typename === 'ClientPendingVoiceTranscript') {
            return (
              <ChatPendingVoiceTranscriptBubble
                key={getEventKey(event, index)}
                event={event}
                ref={
                  event.role === LLMRole.USER && index === lastUserMessageIndex
                    ? updateLatestUserMessageElement
                    : null
                }
              />
            );
          }
          switch (event.role) {
            case LLMRole.USER:
              return (
                <ChatUserMessageBubble
                  key={getEventKey(event, index)}
                  event={event}
                  chatSessionId={chatSessionId}
                  messageSenderContext={messageSenderContext}
                  sessionCreatorFallback={sessionCreatorFallback}
                  isFirstMessageInThread={getEventKey(event, index) === firstDisplayMessageKey}
                  showCurrentUserSender={showCurrentUserSender}
                  ref={index === lastUserMessageIndex ? updateLatestUserMessageElement : null}
                />
              );
            case LLMRole.ASSISTANT: {
              // A write-approval request renders like a normal assistant message, with a distinct
              // approval system card that ChatAssistantMessageBubble derives from the event itself.
              // The thread has the full event list, so it computes whether the request has been
              // answered by a user decision that references it.
              return (
                <ChatAssistantMessageBubble
                  key={getEventKey(event, index)}
                  event={event}
                  readonly={readonly}
                  progressData={progressData ?? undefined}
                  citationData={citationData ?? undefined}
                  chatSessionId={chatSessionId}
                  chatSessionDisplayName={chatSessionDisplayName}
                  approvalAnsweredResponse={
                    event.__typename === 'DisplayMessageChatEventNode'
                      ? getAnsweredApprovalStatus(displayEvents, event.id)
                      : null
                  }
                  askUserReply={getAskUserReply(displayEvents, index)}
                  hasChatScopedWriteApproval={hasChatScopedWriteApproval}
                  messageSenderContext={messageSenderContext}
                  sessionCreatorFallback={sessionCreatorFallback}
                />
              );
            }
            default:
              assertNever(event);
              // In case we introduce a new LLMRole on the backend, we fallback to the same rendering as an
              // assistant message.
              return (
                <ChatAssistantMessageBubble
                  key={getEventKey(event, index)}
                  event={event}
                  readonly={readonly}
                  progressData={progressData ?? undefined}
                  citationData={citationData ?? undefined}
                  chatSessionId={chatSessionId}
                  chatSessionDisplayName={chatSessionDisplayName}
                  approvalAnsweredResponse={null}
                  askUserReply={null}
                  hasChatScopedWriteApproval={hasChatScopedWriteApproval}
                  messageSenderContext={messageSenderContext}
                  sessionCreatorFallback={sessionCreatorFallback}
                />
              );
          }
        })}
        {actionButton}
      </div>
    );
  }
);

const styles = StyleSheet.create({
  threadContainer: {
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    width: '100%',
    padding: '16px',
    gap: '16px',
  },
});
